import errorReporter from '@app/errorReporter';
import type { AssetFolderDoc } from '@bb/domain/dist/schemas';
import {
  Button,
  Flex,
  Form,
  Input,
  SemanticColor,
  Switch,
  Title,
} from '@bb/elements-web';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteFolder, saveFolder } from 'api/AssetController';
import { message, Modal } from 'components';
import useBreakpoint from 'hooks/useBreakpoint';
import React, { useEffect, useMemo } from 'react';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import { ASSET_MODAL_LG_WIDTH } from '../constants';

export interface FolderModalProps {
  visible: boolean;
  folder?: Partial<AssetFolderDoc>;
  onClose: () => void;
}

const FolderModal: React.FC<FolderModalProps> = ({
  visible,
  folder,
  onClose,
}) => {
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const isEdit = !!folder?.id;
  const screen = useBreakpoint();
  const isLessThanLarge = !screen.lg;

  const parent = useMemo(
    () => folder?.parent && String(folder.parent),
    [folder?.parent],
  );

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        name: folder?.name || '',
        adminOnly: folder?.adminOnly || false,
      });
    }
  }, [visible, folder, form]);

  // Save folder mutation
  const saveFolderMutation = useMutation({
    mutationFn: (values: { name: string; adminOnly: boolean }) => {
      const folderData = {
        ...folder,
        name: values.name,
        adminOnly: values.adminOnly,
        parent,
      };
      return saveFolder(folderData as any);
    },
    onSuccess: () => {
      message.success(`Folder ${isEdit ? 'updated' : 'created'} successfully`);
      queryClient.invalidateQueries({ queryKey: ['folders'] });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      onClose();
    },
    onError: () => {
      message.error('Failed to save folder');
    },
  });

  // Delete folder mutation
  const deleteFolderMutation = useMutation({
    mutationFn: (folderId: string) => deleteFolder(folderId),
    onSuccess: () => {
      message.success('Folder deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['folders'] });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      onClose();
    },
    onError: () => {
      message.error('Failed to delete folder');
    },
  });

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      saveFolderMutation.mutate(values);
    } catch (error) {
      errorReporter.error('Failed to validate form:', error);
    }
  };

  const handleDelete = () => {
    if (!folder?.id) return;

    Swal.fire({
      title: 'Delete Folder',
      text: 'Are you sure you want to permanently delete this folder and all of its contents?',
      type: 'warning',
      showCancelButton: true,
      confirmButtonColor: SemanticColor.success,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    }).then(async result => {
      if (result.value) {
        deleteFolderMutation.mutate(folder.id);
      }
    });
  };

  const loading =
    saveFolderMutation.isPending || deleteFolderMutation.isPending;

  return (
    <Modal
      centered
      width={isLessThanLarge ? '85%' : ASSET_MODAL_LG_WIDTH}
      destroyOnClose
      title={
        <StyledModalTitle
          dataCy="folder-modal-title"
          level={3}
          thickness="normal"
        >
          {isEdit ? 'Manage' : 'Create'} Folder
        </StyledModalTitle>
      }
      open={visible}
      onCancel={onClose}
      confirmLoading={loading}
      footer={[
        <Flex
          key="1-folder-modal-footer-container"
          align="center"
          justify="flex-start"
        >
          {isEdit && (
            <Button
              key="1-delete-button"
              minWidth={65}
              danger
              size="small"
              onClick={handleDelete}
              dataCy="folder-modal-delete-button"
            >
              Trash
            </Button>
          )}
          <StyledFooterButtonContainer>
            <Button
              dataCy="folder-modal-cancel-button"
              minWidth={65}
              size="small"
              key="2-cancel-button"
              type="secondary"
              color="default"
              onClick={onClose}
            >
              Close
            </Button>
            <Button
              key="3-save-button"
              minWidth={65}
              size="small"
              type="primary"
              loading={loading}
              onClick={handleSave}
              dataCy="folder-modal-save-button"
            >
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </StyledFooterButtonContainer>
        </Flex>,
      ]}
    >
      <Form
        dataCy="folder-modal-form"
        form={form}
        layout="horizontal"
        onFinish={handleSave}
      >
        <Form.Item
          name="name"
          label="Folder Name"
          rules={[{ required: true, message: 'Please enter a folder name.' }]}
        >
          <Input dataCy="folder-modal-name-input" placeholder="Folder Name" />
        </Form.Item>
        <Form.Item
          name="adminOnly"
          valuePropName="checked"
          label="Admin Only Folder"
        >
          <Switch dataCy="folder-modal-admin-only-switch" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default FolderModal;

const StyledModalTitle = styled(Title)`
  && {
    margin: 0;
  }
`;

const StyledFooterButtonContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  gap: 6px;
`;
